import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import { HashRouter, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { GlobalStyles } from './components/GlobalStyles';
import { PageLoader } from './components/PageLoader';
import { Header } from './components/Header';
import { Footer } from './components/sections/Footer';
import { DS } from './components/ds';
import { PromoModal } from './components/PromoModal';
import { HomePage } from './pages/HomePage';
import { NosotrosPage } from './pages/NosotrosPage';
import { FAQPage } from './pages/FAQPage';
import { BlogPage } from './pages/blog/BlogPage';
import { ArticleDetailPage } from './pages/blog/ArticleDetailPage';
import { useStructuredData } from './hooks/useStructuredData';
import { useGoogleAnalyticsPageViews } from './hooks/useGoogleAnalytics';
type View = 'landing' | 'nosotros' | 'faq' | 'blog';
function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const isBlogRoute = location.pathname.startsWith('/blog');
  const isNosotrosRoute = location.pathname === '/nosotros';
  const isFaqRoute = location.pathname === '/faq';
  const currentView: View = isBlogRoute ? 'blog' : isNosotrosRoute ? 'nosotros' : isFaqRoute ? 'faq' : 'landing';
  useStructuredData();
  useGoogleAnalyticsPageViews();
  useEffect(() => {
    document.documentElement.lang = 'es-CO';
  }, []);
  useLayoutEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    const scrollToHero = () => window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto'
    });
    scrollToHero();
    const frame = window.requestAnimationFrame(scrollToHero);
    window.addEventListener('pageshow', scrollToHero);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('pageshow', scrollToHero);
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);
  const scrollTo = useCallback((id: string) => {
    if (id === 'blog') {
      navigate('/blog');
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }
    if (id === 'nosotros') {
      navigate('/nosotros');
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }
    if (id === 'faq') {
      navigate('/faq');
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }
    if (id === 'top') {
      if (isBlogRoute || isNosotrosRoute || isFaqRoute) navigate('/');
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }
    const scrollToSection = () => {
      const element = document.getElementById(id);
      if (!element) return;
      window.scrollTo({
        top: element.getBoundingClientRect().top + window.scrollY - 68,
        behavior: 'smooth'
      });
    };
    if (isBlogRoute || isNosotrosRoute || isFaqRoute) {
      navigate('/');
      window.setTimeout(scrollToSection, 100);
      return;
    }
    scrollToSection();
  }, [isBlogRoute, isNosotrosRoute, isFaqRoute, navigate]);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('section-visible');
        observer.unobserve(entry.target);
      });
    }, {
      threshold: 0.08
    });
    const sections = document.querySelectorAll('section[id]');
    sections.forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, [isBlogRoute, isNosotrosRoute, isFaqRoute]);
  const goHome = () => {
    if (isBlogRoute || isNosotrosRoute || isFaqRoute) navigate('/');
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return <div className="alinova-root" style={{
    fontFamily: DS.font.body,
    background: DS.color.ivory
  }} data-id="element-0" data-mp="6nmbs08tvs34">
      <PageLoader data-id="element-1" data-mp="arbyek5xzzli" />
      <GlobalStyles data-id="element-2" data-mp="ano9xzfcu3dn" />
      <Header onNav={scrollTo} currentView={currentView} data-id="element-3" data-mp="qxnmbmr1wgvl" />

      <Routes data-id="element-4" data-mp="qz8fb7xpvrrc">
        <Route path="/" element={<HomePage onCTA={scrollTo} data-id="element-6" data-mp="m5x5cxxlyuv7" />} data-id="element-5" data-mp="m6fbqndzipgu" />
        <Route path="/nosotros" element={<NosotrosPage onBack={goHome} data-id="element-8" data-mp="h7c54yicwb6r" />} data-id="element-7" data-mp="m4ujj4f57im5" />
        <Route path="/faq" element={<FAQPage onBack={goHome} data-id="element-10" data-mp="h7lxutuscwg5" />} data-id="element-9" data-mp="h8drhz4tok3s" />
        <Route path="/blog" element={<BlogPage data-id="element-12" data-mp="c9anmi0kwjxe" />} data-id="element-11" data-mp="c8j1dxsvfyjs" />
        <Route path="/blog/:id" element={<ArticleDetailPage data-id="element-14" data-mp="19vzvv9lvjsrp" />} data-id="element-13" data-mp="c9u7435vngdj" />
      </Routes>

      <Footer onNav={scrollTo} data-id="element-15" data-mp="19uygisxtsk46" />

      <div style={{
      position: 'fixed',
      bottom: '28px',
      right: '28px',
      zIndex: 9998
    }} onMouseEnter={event => toggleWhatsAppLabel(event.currentTarget, true)} onMouseLeave={event => toggleWhatsAppLabel(event.currentTarget, false)} data-id="element-16" data-mp="19tx6mrsjq3o1">
        <div className="wa-label" aria-hidden="true" style={{
        position: 'absolute',
        right: '78px',
        top: '50%',
        transform: 'translateY(-50%) translateX(8px)',
        background: DS.color.inkDeep,
        color: DS.color.aquaLight,
        fontFamily: DS.font.body,
        fontSize: '0.78rem',
        fontWeight: 600,
        letterSpacing: '0.06em',
        whiteSpace: 'nowrap',
        padding: '8px 14px',
        borderRadius: '8px',
        opacity: 0,
        pointerEvents: 'none',
        transition: `opacity 0.22s ${DS.ease.smooth}, transform 0.22s ${DS.ease.smooth}`,
        boxShadow: DS.shadow.card
      }} data-id="element-17" data-mp="vvgps80yyws6">
          Escríbenos
          <span style={{
          position: 'absolute',
          right: '-6px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: 0,
          height: 0,
          borderTop: '5px solid transparent',
          borderBottom: '5px solid transparent',
          borderLeft: `6px solid ${DS.color.inkDeep}`
        }} data-id="element-18" data-mp="2jsyb71wmdmem" />
        </div>

        <a href={DS.wa} target="_blank" rel="noopener noreferrer" aria-label="Contactar por WhatsApp" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '64px',
        height: '64px',
        borderRadius: '50%',
        background: '#25D366',
        border: '1.5px solid #25D366',
        boxShadow: '0 10px 30px rgba(37, 211, 102, 0.35)',
        textDecoration: 'none',
        transition: `transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)`,
        position: 'relative'
      }} onFocus={event => toggleWhatsAppLabel(event.currentTarget.parentElement, true)} onBlur={event => toggleWhatsAppLabel(event.currentTarget.parentElement, false)} onMouseEnter={event => {
        event.currentTarget.style.transform = 'scale(1.15)';
        event.currentTarget.style.boxShadow = '0 14px 40px rgba(37, 211, 102, 0.45)';
        event.currentTarget.style.background = '#1EBE5D';
        event.currentTarget.style.borderColor = '#1EBE5D';
      }} onMouseLeave={event => {
        event.currentTarget.style.transform = 'scale(1)';
        event.currentTarget.style.boxShadow = '0 10px 30px rgba(37, 211, 102, 0.35)';
        event.currentTarget.style.background = '#25D366';
        event.currentTarget.style.borderColor = '#25D366';
      }} data-id="element-19" data-mp="yh6jcwx4qan6">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true" data-id="element-20" data-mp="2pez6ggh5pjmw">
            <path d="M16 3C9.373 3 4 8.373 4 15c0 2.385.668 4.61 1.832 6.505L4 29l7.697-1.807A11.94 11.94 0 0016 27c6.627 0 12-5.373 12-12S22.627 3 16 3z" fill="white" opacity="0.2" data-id="element-21" data-mp="2pd4j6viexffn" />
            <path d="M16 3C9.373 3 4 8.373 4 15c0 2.385.668 4.61 1.832 6.505L4 29l7.697-1.807A11.94 11.94 0 0016 27c6.627 0 12-5.373 12-12S22.627 3 16 3z" stroke="white" strokeWidth="1.2" fill="none" data-id="element-22" data-mp="2kg4dwmte0gpl" />
            <path d="M22.003 18.837c-.306-.153-1.809-.893-2.089-.994-.28-.1-.484-.153-.688.153-.203.306-.79.994-.968 1.198-.178.203-.356.229-.662.076-.306-.153-1.292-.476-2.461-1.518-.91-.811-1.524-1.813-1.702-2.119-.178-.306-.019-.471.134-.623.137-.137.306-.357.459-.535.153-.178.204-.306.306-.51.102-.203.051-.382-.025-.535-.077-.153-.688-1.66-.942-2.272-.248-.596-.5-.515-.688-.524l-.586-.01c-.203 0-.535.077-.815.382-.28.306-1.07 1.045-1.07 2.549s1.096 2.957 1.248 3.161c.153.203 2.156 3.291 5.226 4.615.73.315 1.3.503 1.744.644.733.233 1.4.2 1.927.121.588-.088 1.809-.74 2.064-1.454.255-.714.255-1.326.178-1.454-.077-.127-.28-.203-.586-.357z" fill="white" data-id="element-23" data-mp="2qx7k3xh6w32d" />
          </svg>
          <span aria-hidden="true" style={{
          position: 'absolute',
          inset: '-4px',
          borderRadius: '50%',
          border: `1px solid ${DS.color.aqua}`,
          animation: 'waPulse 2.5s ease-out infinite',
          pointerEvents: 'none',
          opacity: 0.4
        }} data-id="element-24" data-mp="1u6f1ot6s9yxf" />
        </a>
      </div>
      <PromoModal data-id="element-25" data-mp="nof9s4qifphf" />
    </div>;
}
export function App() {
  return <HashRouter data-id="element-26" data-mp="nmky7h14p9q0">
      <AppShell data-id="element-27" data-mp="3m6b74ahu9ucq" />
    </HashRouter>;
}
function toggleWhatsAppLabel(container: HTMLElement | null, isVisible: boolean) {
  const label = container?.querySelector<HTMLElement>('.wa-label');
  if (!label) return;
  label.style.opacity = isVisible ? '1' : '0';
  label.style.transform = isVisible ? 'translateY(-50%) translateX(0)' : 'translateY(-50%) translateX(8px)';
  label.style.pointerEvents = isVisible ? 'auto' : 'none';
}